import GeoJSON from 'ol/format/GeoJSON';
import OlPolygon from 'ol/geom/Polygon';
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Stroke, Style } from 'ol/style';
import KML from 'ol/format/KML';
import { Projection } from 'ol/proj';
import { CalcSurface } from '../../../../../../typings/reality-capture/volumes/models';
import { TreeNode } from '@shared/ui-native/Tree';
import { Ajax } from '@shared/Ajax';
import Feature from 'ol/Feature';
import OlMultiPolygon from 'ol/geom/MultiPolygon';
import { latLonToEN } from '@openlayers/ol-utils';
import { layerStore } from '@shared/openlayers/tools/volume/utils/store/layer-store';
import { Auth } from '@shared/Auth';
import { alignmentToPoints, pointsToFeature } from './cutting';
import type { CorridorSide } from './cutting';

export { splitCorridorByCenterline, splitOpenlayersFeatureIntoSegments } from './cutting';
export type { CorridorSide } from './cutting';

/**
 * Fetches the polygon outline of a model and uses it as the drawn area for volume calculation.
 * The outline is read from /kuura/v1/viz/model/{uuid}/outlines and projected to EPSG:3857.
 */
export async function fetchModelOutlineArea(toolsStore: unknown, modelUuid: string): Promise<void> {
    try {
        const token = await Auth.getToken();
        const response = await fetch(`/kuura/v1/viz/model/${modelUuid}/outlines`, {
            headers: { Authorization: 'Bearer ' + token.accessToken },
        });

        if (!response.ok) {
            console.error('Failed to fetch model outline:', response.status);
            return;
        }

        const outlineResponse = await response.json();
        const features = new GeoJSON().readFeatures(outlineResponse, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
        });

        if (!features.length) {
            console.warn('No outline features found for model:', modelUuid);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vol = (toolsStore as any).volumeTool;

        // Clear any previously pending (unprocessed) drawn features
        const [prevPending] = vol.pendingFeatures.splice(0);
        if (prevPending) vol.removeMeasurement?.(prevPending);

        // KmlExport.getFile only handles Polygon geometry (3 coordinate levels).
        // MultiPolygon has an extra nesting level which makes olTransform receive arrays
        // instead of coordinate pairs, producing NaN. Explode MultiPolygon → Polygons first.
        const polygonFeatures: Feature<OlPolygon>[] = features.flatMap(f => {
            const geom = f.getGeometry();
            if (geom instanceof OlMultiPolygon) {
                return geom.getPolygons().map(poly => new Feature({ geometry: poly }));
            }
            return [f as Feature<OlPolygon>];
        });

        for (const feature of polygonFeatures) {
            feature.set('finished', true);
            if (vol.drawLayer) {
                vol.drawLayer.getSource().addFeature(feature);
            }
            vol.pendingFeatures.push(feature);
            if (vol.bindEvents) vol.bindEvents(feature);
        }

        layerStore.incrementDrawCount();
    } catch (e) {
        console.error('Error fetching model outline area:', e);
    }
}

export function determineTargetArea(surface1Type: string, surface2Type: string): number {
    return surface1Type === 'dem' && surface2Type === 'dem' ? 30000 : 5000;
}

export function kmlToFeatures(kmlString: string, sourceProj: Projection) {
    const kmlFormat = new KML();
    const features = kmlFormat.readFeatures(kmlString, {
        featureProjection: sourceProj,
    });
    console.log('features', features);
    return features;
}

export function calcReference(volume): CalcSurface {
    const calcReference = {
        text: volume.from_name,
        surfaceType: volume.from_type,
        surfaceDemUrl: volume.from_s3_key,
        surfaceModelUuid: volume.from_model_uuid,
    };
    return calcReference;
}

export function calcTarget(volume): CalcSurface {
    const calcTarget = {
        text: volume.to_name,
        surfaceType: volume.to_type,
        surfaceDemUrl: volume.to_s3_key,
        surfaceModelUuid: volume.to_model_uuid,
    };
    return calcTarget;
}

export function treeNodeToCalcSurface(node: TreeNode): CalcSurface {
    return {
        surfaceType: 'model',
        surfaceModelUuid: (node as TreeNode).uuid,
        text: (node as TreeNode).text,
    };
}

export function vizSegments(splitSegments, mapWrapper, olFeature): boolean {
    const visualizationLayer = new VectorLayer({
        source: new VectorSource({
            features: splitSegments,
        }),
        style: new Style({
            stroke: new Stroke({
                color: 'rgba(0, 0, 255, 0.5)',
                width: 1,
            }),
        }),
    });
    mapWrapper.getInstance().addLayer(visualizationLayer);
    olFeature.set('visualizationLayer', visualizationLayer);
    return true;
}

export function setHeatmap(mapWrapper, olFeature, heatmap_url): void {
    console.log('setHeatmap', olFeature, heatmap_url);
    // Show heatmap of the volume calculation in openlayers
    const heatmapLayer = olFeature.get('heatmapLayer');
    if (heatmapLayer) {
        mapWrapper.getInstance().removeLayer(heatmapLayer);
        olFeature.set('heatmapLayer', null);
    }

    const geom = olFeature.getGeometry();
    const extent = geom.getExtent();
    const imageLayer = new ImageLayer({
        source: new ImageStatic({
            url: heatmap_url,
            imageExtent: extent,
            crossOrigin: 'anonymous',
        }),
    });
    imageLayer.setZIndex(9999);
    mapWrapper.getInstance().addLayer(imageLayer);
    olFeature.set('heatmapLayer', imageLayer);
}

export async function generateAlignmentArea(
    toolsStore,
    alignmentModelId: number,
    alignmentIndex: number,
    startStation: string | number,
    endStation: string | number,
    corridorWidth: number,
    side: CorridorSide = 'both'
) {
    if (alignmentModelId <= 0 || startStation === '' || endStation === '') return;
    try {
        const alignResp = await Ajax({ url: 'ajax_alignments.json', data: { id: alignmentModelId } });
        const alignment = alignResp.alignments.find(a => (a.index || 0) === alignmentIndex);

        const resp = await Ajax({
            url: 'ajax_alignment_stations.json',
            data: {
                alignmentId: alignmentModelId,
                alignmentIndex: alignmentIndex,
                projectId: (window as any).kuura.getProjectId(),
            },
            cache: true,
        });

        console.log('Alignment stations:', resp);

        const stations = resp.coords.length ? resp.coords : resp.stationEquations;
        if (!stations || !stations.length) {
            return;
        }

        const isEquation = typeof stations[0].station === 'string';
        let points = [];

        if (isEquation) {
            // Find start point
            for (let i = 0; i < stations.length - 1; i++) {
                const val1 = parseFloat(stations[i].station);
                const val2 = parseFloat(stations[i + 1].station);
                if (val1 <= Number(startStation) && val2 >= Number(startStation)) {
                    const ratio = ((startStation as number) - val1) / (val2 - val1);
                    const lon = stations[i].lon + ratio * (stations[i + 1].lon - stations[i].lon);
                    const lat = stations[i].lat + ratio * (stations[i + 1].lat - stations[i].lat);
                    points.push([lon, lat]);
                    break;
                }
            }

            // Add middle points
            for (const st of stations) {
                const val = parseFloat(st.station);
                if (!isNaN(val) && val > Number(startStation) && val < Number(endStation)) {
                    points.push([st.lon, st.lat]);
                }
            }

            // Find end point
            for (let i = 0; i < stations.length - 1; i++) {
                const val1 = parseFloat(stations[i].station);
                const val2 = parseFloat(stations[i + 1].station);
                if (val1 <= Number(endStation) && val2 >= Number(endStation)) {
                    const ratio = ((endStation as number) - val1) / (val2 - val1);
                    const lon = stations[i].lon + ratio * (stations[i + 1].lon - stations[i].lon);
                    const lat = stations[i].lat + ratio * (stations[i + 1].lat - stations[i].lat);
                    points.push([lon, lat]);
                    break;
                }
            }
        } else {
            points = await alignmentToPoints(alignment, startStation, endStation, stations);
        }

        if (points.length < 2) {
            console.warn('Not enough points found for the given stations.');
            return;
        }

        // Apply it as the drawn area
        const feature = await pointsToFeature(points, corridorWidth, side);
        feature.set('finished', true);
        // Store alignment metadata so getCutFill can split along the alignment instead of a grid
        feature.set(
            'alignmentEnPoints',
            points.map(p => latLonToEN(p[1], p[0]))
        );
        feature.set('alignmentCorridorWidth', corridorWidth);
        feature.set('alignmentStartStation', Number(startStation));
        feature.set('alignmentSide', side);

        const volumeTool = toolsStore.volumeTool;

        // Drop a previous corridor that is still waiting for its first calculation
        const [prevPending] = volumeTool.pendingFeatures.splice(0);
        if (prevPending) volumeTool.removeMeasurement(prevPending);

        volumeTool.drawLayer.getSource().addFeature(feature);
        volumeTool.bindEvents(feature);

        // If the panel already produced a measurement, recalculate it in place
        // (keeping id/uuid/name) instead of adding a new volume to the list
        const prevFeature = volumeTool.activeAlignmentFeature;
        volumeTool.activeAlignmentFeature = feature;
        const replaced = prevFeature ? await volumeTool.replaceVolume(prevFeature, feature) : false;
        if (!replaced) {
            volumeTool.pendingFeatures.push(feature);
            layerStore.incrementDrawCount();
        }
    } catch (e) {
        console.error('Error generating alignment area:', e);
    }
}
